import { serverErrorMessage } from './apiErrors';

/**
 * ⚠️ [magánóra-sorozat, 2026-10-31] TESZT-REFERENCIA — a TERMELÉSI KÓD MÁR NEM SZÁMOL ITT NAPOT.
 *
 * A sorozat napjait ma a SZERVER számolja (`backend/PinyoApi/Services/RecurringSeriesDates.cs`), a web egyetlen
 * `POST /api/events/series` (létrehozás) ill. `POST /api/events/series/{groupId}/extend` (bővítés) hívással dolgozik,
 * az előnézet a szerver `dryRun` válaszából jön (`components/Calendar/SeriesPreview.tsx`). Ezért a fájl dátum-
 * függvényei (`dayNumber`, `weeksSinceStart`, `recurringEventDates`, `seriesExtensionDates`) és a régi N-POST út
 * segédei (`createSeriesEvents`, `describeSeriesFailures`) a felületről KIVEZETVE; CSAK a tesztek használják:
 *   • PARITÁS: `utils/__tests__/recurringSeries.test.ts` a `Documents/fixtures/recurring-series-cases.json`-t
 *     futtatja — ugyanezt a fixture-t az xUnit is (`EventSeriesTests`), tehát a hétfő-alapú szabály két
 *     független megvalósításban mérten azonos;
 *   • `components/Calendar/__tests__/recurringInterval.test.ts`.
 * A felület ebből ma csak a NAPNEVEKET (`DAY_NAMES_BY_GET_DAY`, `DAY_NAMES_MONDAY_FIRST`) és a `fromIsoDate`-et
 * használja. A fájl SZÁNDÉKOSAN nincs törölve (a szabály itt olvasható referenciaként; ha a szabály változik,
 * a fixture-t a `Documents/fixtures/gen-recurring-series-fixture.js` generálja újra ebből a fájlból).
 *
 * Eredeti leírás: ismétlődő sorozatok napjainak kiszámítása — az esemény-létrehozás és a sorozat-bővítés
 * közös helye volt. (Korábban az EventCreateDialog-ban volt.)
 */

/**
 * Egy naptári nap sorszáma (UTC-nap a helyi év/hónap/nap komponensekből).
 *
 * MIÉRT: a sorozat napról napra lép, és a helyi idő (00:00) a nyári időszámítás-váltáskor
 * eltolódhat — a nyers milliszekundum-kivonás 6,96 napot adna. Nap-sorszámmal ez nem
 * fordulhat elő.
 */
export function dayNumber(d: Date): number {
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
}

/**
 * Hétfővel kezdődő hetek száma a kezdődátum hetéhez képest (0 = a kezdés hete).
 * A gyakorisághoz („kéthetente") kell: csak azokban a hetekben jön létre esemény, amelyek a
 * gyakoriságra esnek.
 */
export function weeksSinceStart(startDate: Date, date: Date): number {
  const weekStartDayNumber = (d: Date) => dayNumber(d) - ((d.getDay() + 6) % 7); // 0 = hétfő

  return (weekStartDayNumber(date) - weekStartDayNumber(startDate)) / 7;
}

/** Magyar napnevek a JS `getDay()` sorrendjében (0 = vasárnap). */
export const DAY_NAMES_BY_GET_DAY = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];

export const DAY_NAMES_MONDAY_FIRST = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

/** `YYYY-MM-DD` a helyi naptári napból. */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DD` → helyi éjféli Date (időzóna-csúszás nélkül). */
export function fromIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Egy ismétlődő sorozat NAPJAI, `YYYY-MM-DD` alakban, a kezdődátumtól a végdátumig
 * (mindkettő beleértve), a kiválasztott napokon, a gyakoriságra eső hetekben.
 */
export function recurringEventDates(
  startDate: Date,
  endDate: Date,
  selectedDayNames: string[],
  intervalWeeks: number
): string[] {
  const dates: string[] = [];
  const currentDate = new Date(startDate);
  const lastDayNumber = dayNumber(endDate);

  while (dayNumber(currentDate) <= lastDayNumber) {
    const dayName = DAY_NAMES_BY_GET_DAY[currentDate.getDay()];
    // Csak a gyakoriságra eső hetekben (1 = minden hét, 2 = kéthetente, ...).
    const onInterval = weeksSinceStart(startDate, currentDate) % intervalWeeks === 0;

    if (selectedDayNames.includes(dayName) && onInterval) {
      dates.push(toIsoDate(currentDate));
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
}

export interface SeriesExtensionInput {
  /** A sorozat ELSŐ előfordulásának napja — ehhez igazodik a „kéthetente" ritmus. */
  anchorDate: string;
  /** Az új végdátum (beleértve). */
  endDate: string;
  selectedDayNames: string[];
  intervalWeeks: number;
  /** A sorozat MÁR LÉTEZŐ napjai (`YYYY-MM-DD`) — ezekre nem hozunk létre újat. */
  existingDates: string[];
  /** Ennél korábbi napra nem hozunk létre eseményt (jellemzően a mai nap). */
  notBefore: string;
}

/**
 * Egy meglévő sorozat BŐVÍTÉSÉNEK új napjai: a ritmus a sorozat első napjához igazodik, a már
 * létező és a múltbeli napok kimaradnak. Törlés soha — az a meglévő előfordulások egyedi
 * művelete marad (rajtuk már lehet jelenléti ív és kifizetés). [klub-döntés 2026-09-11]
 */
export interface SeriesCreateResult {
  created: string[];
  /** `YYYY-MM-DD (szerver üzenete)` — a felhasználónak megmutatjuk, NEM nyeljük le. */
  failed: string[];
}

/**
 * Sorozat alkalmainak létrehozása EGYENKÉNT, a bukásokat összegyűjtve.
 *
 * MIÉRT: a szerver egy-egy napot elutasíthat (pl. asztal-ütközés, kevés asztal a helyszínen —
 * Budaörsön 15 kért asztal 13-ból), a többi meg létrejön. Korábban ez csak a konzolba ment, és a
 * felhasználó „10 esemény létrehozva"-t olvasott, miközben 3 hiányzott. [ügyfél-hiba 2026-09]
 */
export async function createSeriesEvents<T extends { eventDate: string }>(
  dates: string[],
  buildRequest: (eventDate: string) => T,
  create: (request: T) => Promise<unknown>
): Promise<SeriesCreateResult> {
  const result: SeriesCreateResult = { created: [], failed: [] };
  for (const eventDate of dates) {
    try {
      await create(buildRequest(eventDate));
      result.created.push(eventDate);
    } catch (err: any) {
      // [Web UX 3. csomag (D), 2026-10-04] a szerver indoklása mindhárom törzs-alakból (a burkolt `error.message` és a
      // validációs `errors` is), nem csak a csupasz `message`-ből.
      const reason = serverErrorMessage(err?.response?.data);
      result.failed.push(`${eventDate}${reason ? ` (${reason})` : ''}`);
    }
  }
  return result;
}

/**
 * Egységes hibaszöveg részleges bukásra — a létrehozás és a bővítés ugyanazt mondja.
 * `hint`: mit tegyen a felhasználó (a hívó tudja, hogy nála az újrapróbálás biztonságos-e).
 */
export function describeSeriesFailures(
  result: SeriesCreateResult,
  hint = 'A sikertelen napokat próbáld újra, vagy hozd létre egyenként.'
): string {
  const lead = result.created.length === 0
    ? `Egy alkalom sem jött létre (${result.failed.length} nap): `
    : `${result.created.length} alkalom létrejött, ${result.failed.length} nem: `;
  return `${lead}${result.failed.join(', ')}. ${hint}`;
}

export function seriesExtensionDates(input: SeriesExtensionInput): string[] {
  const existing = new Set(input.existingDates);
  return recurringEventDates(
    fromIsoDate(input.anchorDate),
    fromIsoDate(input.endDate),
    input.selectedDayNames,
    input.intervalWeeks
  ).filter(d => !existing.has(d) && d >= input.notBefore);
}
